/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    ArrowRight,
    Menu,
  } from "lucide-react";
  import { Link } from "react-router-dom";
import Footer from '../components/Footer';

  // These are all the smaller components needed for the About page

// The TabButton component used in the navigation
const TabButton = ({ active, onClick, label, icon }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm md:text-base transition-colors flex items-center whitespace-nowrap ${
        active 
          ? 'bg-blue-600 text-white' 
          : 'text-white/70 hover:text-white hover:bg-white/10'
      }`}
    >
      <span className="mr-2">{icon}</span>
      {label}
    </button>
  );
  
  // Tab content container with animations
  const TabContent = ({ children }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-[400px]"
    >
      {children}
    </motion.div>
  );
  
  // Component for feature highlights
  const Highlight = ({ icon, text }) => (
    <motion.div 
      className="flex items-start"
      whileHover={{ x: 3 }}
      transition={{ duration: 0.2 }}
    >
      <div className="text-yellow-500 mr-3 mt-0.5 flex-shrink-0">
        {icon}
      </div>
      <p className="text-white/90 text-sm">{text}</p>
    </motion.div>
  );
  
  // Component for game mechanics
  const Mechanic = ({ title, description }) => (
    <div className="bg-blue-950/50 p-3 rounded-lg border border-blue-800/30">
      <h4 className="font-medium text-blue-300 mb-1">{title}</h4>
      <p className="text-sm text-white/80">{description}</p>
    </div>
  );
  
  // Team member card
  const TeamMember = ({ name, role, bio, delay }) => (
    <motion.div
      className="bg-slate-800/70 rounded-xl p-5 border border-slate-700"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
          <UserIcon className="w-8 h-8 text-blue-400" />
        </div>
      </div>
      <h3 className="font-bold text-lg mb-1">{name}</h3>
      <div className="text-blue-400 text-sm mb-3">{role}</div>
      <p className="text-white/70 text-sm">{bio}</p>
    </motion.div>
  );
  
  // Icon components
  const StarIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
  
  const TrophyIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
      <path d="M4 22h16"></path>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
    </svg>
  );
  
  const GameIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect>
      <polyline points="17 2 12 7 7 2"></polyline>
    </svg>
  );
  
  const StoryIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
    </svg>
  );
  
  const FeatIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="12" r="10"></circle>
      <path d="m16 12-4-4-4 4"></path>
      <path d="M12 16V8"></path>
    </svg>
  );
  
  const TeamIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      <path d="M21 21v-2a4 4 0 0 0-3-3.85"></path>
    </svg>
  );
  
  const UserIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="8" r="5"></circle>
      <path d="M20 21a8 8 0 1 0-16 0"></path>
    </svg>
  );
  
  const PlayIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polygon points="10 8 16 12 10 16 10 8"></polygon>
    </svg>
  );
  
  const BookIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
    </svg>
  );
  
  // Feature Icons
  const BuildIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <rect x="2" y="6" width="20" height="12" rx="2"></rect>
      <path d="M12 12h.01"></path>
      <path d="M17 12h.01"></path>
      <path d="M7 12h.01"></path>
      <path d="M4 3h16"></path>
      <path d="M4 21h16"></path>
    </svg>
  );
  
  const StaffIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  );
  
  const GuestIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <path d="M5 22h14"></path>
      <path d="M5 2h14"></path>
      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path>
      <path d="M14 2v4.5a4 4 0 0 1-4 4 4 4 0 0 1-4-4V2"></path>
    </svg>
  );
  
  const CompetitionIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <path d="M2 12h20"></path>
      <path d="M10 16V8"></path>
      <path d="M2 12a10 10 0 1 1 20 0"></path>
      <path d="M10 12H8"></path>
      <path d="M2 17h2"></path>
      <path d="M20 17h2"></path>
    </svg>
  );
  
  const FacilitiesIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M12 2v20"></path>
      <path d="M2 12h20"></path>
      <path d="M12 9a3 3 0 0 0 0 6"></path>
    </svg>
  );
  
  const FinancesIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <line x1="12" y1="2" x2="12" y2="22"></line>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>
  );

const About = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('game');
  const [showFeatures, setShowFeatures] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Data for features section
  const features = [
    {
      icon: <BuildIcon />,
      title: "Build Your Empire",
      description: "Design and construct a luxurious hotel with restaurants, pools, spas, and even private submarines for your VIP guests.",
      color: "bg-blue-500"
    },
    {
      icon: <StaffIcon />,
      title: "Manage Your Staff",
      description: "Hire, train, and manage staff to ensure exceptional service that keeps your guests coming back.",
      color: "bg-green-500"
    },
    {
      icon: <GuestIcon />,
      title: "Attract Guests",
      description: "From business travelers to families, each guest type has unique needs and preferences you'll need to satisfy.",
      color: "bg-amber-500"
    },
    {
      icon: <CompetitionIcon />,
      title: "Dominate the Market",
      description: "Compete against rival hotels with strategic pricing, marketing campaigns, and unique luxury facilities.",
      color: "bg-purple-500"
    },
    {
      icon: <FacilitiesIcon />,
      title: "Premium Facilities",
      description: "Install Italian gondolas, helipads, and submarine experiences to attract ultra-wealthy clients and boost your reputation.",
      color: "bg-red-500"
    },
    {
      icon: <FinancesIcon />,
      title: "Strategic Finances",
      description: "Balance your books, invest wisely, and ensure your hotel operations remain profitable as you expand.",
      color: "bg-emerald-500"
    }
  ];

  // Trigger the feature animation after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFeatures(true);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-screen text-white">
    {/* Hero Section */}
    <div className="container mx-auto px-4 pt-8 pb-20">
      <header className="flex justify-between items-center py-4 mb-16 relative">
        <div className="flex items-center">
          <img
            src="/logo.png"
            alt="Hotel Hero Logo"
            className="w-10 h-10 object-contain mr-3"
          />

          <h1 className="text-2xl font-bold">
            Hotel<span className="text-orange-500">Clash</span>
          </h1>
        </div>

        <nav>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-blue-500"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          <ul
            className={`flex flex-col md:flex-row text-blue-500 md:space-x-8 absolute md:static top-16 right-0 bg-white md:bg-transparent w-full md:w-auto p-4 md:p-0 shadow md:shadow-none transition-all duration-300 ${
              isOpen ? "block" : "hidden md:flex"
            }`}
          >
            <li>
              <a
                href="#features"
                className="block py-2 md:py-0 hover:text-blue-400 transition-colors"
              >
                Features
              </a>
            </li>
            <li>
              <a
                href="#gameplay"
                className="block py-2 md:py-0 hover:text-blue-400 transition-colors"
              >
                Gameplay
              </a>
            </li>
            <li>
              <a
                href="#video"
                className="block py-2 md:py-0 hover:text-blue-400 transition-colors"
              >
                Video
              </a>
            </li>
            <li>
              <a
                href="#testimonials"
                className="block py-2 md:py-0 hover:text-blue-400 transition-colors"
              >
                Testimonials
              </a>
            </li>
          </ul>
        </nav>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Build Your Empire, <br />
            <span className="text-blue-400">Crush</span> Your Rivals
          </motion.h2>
          <motion.p
            className="text-lg text-gray-300 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Experience the thrill of hotel management in this addictive tycoon
            game. Build luxurious rooms, hire talented staff, and outmaneuver
            your competitors to become the ultimate hospitality mogul.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <Link to="/register">
              <motion.button
                className="px-8 py-3 bg-blue-600 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-blue-700 transition-colors w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Play Now <ArrowRight className="ml-2 h-5 w-5" />
              </motion.button>
            </Link>
            <Link to="/login">
              <motion.button
                className="px-8 py-3 bg-transparent border border-white/30 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-white/10 transition-colors w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Login
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative hidden lg:block"
        >
          {/* Hotel skyline illustration */}
          <div className="relative h-96 w-full">
            {/* Main hotel tower */}
            <motion.div
              className="absolute left-1/2 transform -translate-x-1/2 bottom-0 w-40 h-80 bg-gradient-to-b from-blue-800 to-blue-900 rounded-t-lg shadow-xl"
              initial={{ height: 0 }}
              animate={{ height: 320 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {/* Windows */}
              <div className="grid grid-cols-4 gap-2 p-4 h-full">
                {[...Array(24)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="bg-yellow-100/30 rounded-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 + i * 0.02 }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Left side building */}
            <motion.div
              className="absolute left-1/4 bottom-0 w-32 h-64 bg-gradient-to-b from-slate-700 to-slate-800 rounded-t-lg shadow-lg"
              initial={{ height: 0 }}
              animate={{ height: 256 }}
              transition={{ duration: 0.7, delay: 0.7 }}
            >
              <div className="grid grid-cols-3 gap-2 p-3 h-full">
                {[...Array(15)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="bg-yellow-100/20 rounded-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 + i * 0.03 }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Right side building */}
            <motion.div
              className="absolute right-1/4 bottom-0 w-24 h-48 bg-gradient-to-b from-slate-700 to-slate-800 rounded-t-lg shadow-lg"
              initial={{ height: 0 }}
              animate={{ height: 192 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <div className="grid grid-cols-3 gap-1 p-2 h-full">
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="bg-yellow-100/20 rounded-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 + i * 0.03 }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Stars in the sky */}
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  top: `${Math.random() * 40}%`,
                  left: `${Math.random() * 100}%`,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.2, 1, 0.2] }}
                transition={{
                  repeat: Infinity,
                  duration: 1 + Math.random() * 2,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="mt-24 py-8 px-6 bg-slate-800/50 rounded-xl backdrop-blur-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h3 className="text-4xl font-bold text-blue-400">50+</h3>
            <p className="text-gray-300 mt-2">Upgrade Options</p>
          </motion.div>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <h3 className="text-4xl font-bold text-blue-400">8</h3>
            <p className="text-gray-300 mt-2">Room Types</p>
          </motion.div>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h3 className="text-4xl font-bold text-blue-400">12</h3>
            <p className="text-gray-300 mt-2">Facility Options</p>
          </motion.div>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <h3 className="text-4xl font-bold text-blue-400">∞</h3>
            <p className="text-gray-300 mt-2">Strategic Possibilities</p>
          </motion.div>
        </div>
      </div>
    </div>

      {/* Tab Navigation */}
      <div className="mx-auto max-w-6xl px-4">
        <motion.div 
          className="flex justify-center mb-8 border-b border-white/10 pb-1 gap-1 md:gap-3 overflow-x-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <TabButton 
            active={activeTab === 'game'} 
            onClick={() => setActiveTab('game')}
            label="About the Game"
            icon={<GameIcon />}
          />
          <TabButton 
            active={activeTab === 'story'} 
            onClick={() => setActiveTab('story')}
            label="The Story"
            icon={<StoryIcon />}
          />
          <TabButton 
            active={activeTab === 'features'} 
            onClick={() => setActiveTab('features')}
            label="Features"
            icon={<FeatIcon />}
          />
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'game' && (
            <TabContent key="game">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-400">Welcome to the Ultimate Hotel Simulation</h2>
                  <p className="mb-4 text-white/80">
                    HotelHero is a strategic hotel management simulator where you'll build and run luxurious hotels while competing against rivals for market dominance.
                  </p>
                  <p className="mb-4 text-white/80">
                    From humble beginnings with just a basic lobby, you'll expand your property with restaurants, spas, and even exotic attractions like Italian gondolas and private submarine experiences!
                  </p>
                  <p className="mb-4 text-white/80">
                    Every decision matters - from staff hiring to room pricing, from marketing campaigns to facility investments. Can you build the most prestigious hotel empire and crush your competition?
                  </p>

                  <motion.div 
                    className="flex gap-4 mt-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <button
                      onClick={() => navigate('/register')} 
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors shadow-lg flex items-center"
                    >
                      <PlayIcon className="mr-2" />
                      Start Building
                    </button>
                    <button
                      onClick={() => setActiveTab('features')} 
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors flex items-center"
                    >
                      <FeatIcon className="mr-2" />
                      See Features
                    </button>
                  </motion.div>
                </motion.div>

                <motion.div
                  className="p-4 bg-gradient-to-br from-blue-900/50 to-indigo-900/50 rounded-2xl border border-blue-700/30 shadow-lg relative overflow-hidden"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="absolute top-0 left-0 w-full h-full bg-blue-600/10 
                    bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.1)_0,rgba(59,130,246,0)_70%)]" />
                    
                  <h3 className="text-xl font-bold mb-4 text-white flex items-center">
                    <TrophyIcon className="mr-2 text-yellow-500" />
                    Game Highlights
                  </h3>
                  
                  <ul className="space-y-3">
                    <Highlight icon={<StarIcon />} text="Dynamic market simulation with real competition" />
                    <Highlight icon={<StarIcon />} text="Diverse guest types with unique preferences" />
                    <Highlight icon={<StarIcon />} text="Staff management with skills and happiness" />
                    <Highlight icon={<StarIcon />} text="Over 20 facility types to build and upgrade" />
                    <Highlight icon={<StarIcon />} text="Marketing campaigns and seasonal events" />
                    <Highlight icon={<StarIcon />} text="Financial management with detailed analytics" />
                  </ul>
                  
                  <motion.div 
                    className="mt-4 pt-4 border-t border-blue-700/30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/80">Player Rating</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon 
                            key={i} 
                            className={`w-4 h-4 ${i < 4.5 ? 'text-yellow-400' : 'text-white/30'}`} 
                            fill={i < 4.5 ? 'currentColor' : 'none'} 
                          />
                        ))}
                        <span className="ml-1 text-sm font-medium text-white/90">4.7</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-white/80">Downloads</span>
                      <span className="text-sm font-medium text-white/90">100K+</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </TabContent>
          )}

          {activeTab === 'story' && (
            <TabContent key="story">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                <motion.div 
                  className="md:col-span-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-400">The HotelHero Story</h2>
                  
                  <div className="prose prose-lg prose-invert max-w-none opacity-90">
                    <p>
                      In the bustling heart of Riverview City, opportunity beckons. Once a sleepy tourist destination, the city has transformed into a booming metropolitan hub, attracting business travelers, vacationing families, and luxury seekers from around the world.
                    </p>
                    
                    <p>
                      As a visionary entrepreneur with dreams of building a hospitality empire, you've acquired a modest property in a promising location. It's nothing much yet—just a humble lobby and a handful of basic rooms—but you see its potential.
                    </p>
                    
                    <p>
                      The timing couldn't be better. The city council has just announced a decade-long development plan that will bring international conferences, sports events, and cultural festivals to Riverview. The demand for quality accommodations is about to skyrocket.
                    </p>
                    
                    <p>
                      But you're not the only one with this insight. Rival hoteliers are already making their moves, from budget chains to luxury brands. The race is on to capture market share and establish dominance in this lucrative industry.
                    </p>
                    
                    <p>
                      Will you create a boutique experience with personalized service? Build a luxury palace with extravagant amenities? Or perhaps focus on efficiency and volume with a well-run mid-range establishment?
                    </p>
                    
                    <p>
                      The choice is yours, but one thing is certain: in the world of HotelHero, only the most strategic, adaptable, and customer-focused establishments will thrive. Are you ready to build your empire and crush your rivals?
                    </p>
                  </div>
                </motion.div>
                
                <motion.div
                  className="hidden md:block md:col-span-1 relative"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="sticky top-10">
                    <div className="rounded-2xl bg-gradient-to-br from-purple-900/50 to-blue-900/50 border border-purple-700/30 p-5 shadow-lg">
                      <h3 className="text-lg font-bold mb-3 flex items-center text-purple-300">
                        <BookIcon className="mr-2" />
                        Gameplay Setting
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-blue-300 font-medium mb-1">Location</h4>
                          <p className="text-sm text-white/80">Riverview City, a booming metropolitan hub with stunning waterfront views and a growing tourist economy.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-blue-300 font-medium mb-1">Time Period</h4>
                          <p className="text-sm text-white/80">Present day with a 10-year simulation span. Each in-game "day" represents strategic decisions and growth.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-blue-300 font-medium mb-1">Competition</h4>
                          <p className="text-sm text-white/80">5 major rival hotel chains and numerous smaller competitors, each with different strategies and target markets.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-blue-300 font-medium mb-1">Events</h4>
                          <p className="text-sm text-white/80">Seasonal tourism, business conferences, cultural festivals, and unexpected challenges that impact hotel demand.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </TabContent>
          )}
          
          {activeTab === 'features' && (
            <TabContent key="features">
              <motion.h2 
                className="text-2xl md:text-3xl font-bold mb-6 text-blue-400 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Game Features
              </motion.h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-5 shadow-lg relative overflow-hidden group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={showFeatures ? { 
                      opacity: 1, 
                      y: 0,
                      transition: { delay: 0.2 + index * 0.1 }
                    } : {}}
                    whileHover={{ 
                      y: -5,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <div className={`w-16 h-16 ${feature.color} rounded-2xl mb-4 flex items-center justify-center shadow-lg relative group-hover:scale-110 transition-transform`}>
                      <div className="text-white">
                        {feature.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed">{feature.description}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -bottom-3 -right-3 w-20 h-20 bg-gradient-to-br from-white/0 to-white/5 rounded-full" />
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.div>
                ))}
              </div>
              
              <motion.div 
                className="mt-12 bg-gradient-to-r from-blue-900/50 to-indigo-900/50 rounded-2xl p-6 border border-blue-500/20 max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <h3 className="text-xl font-bold mb-4 text-center text-white">Advanced Game Mechanics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-4">
                    <Mechanic 
                      title="Dynamic Market Simulation" 
                      description="A realistic economic model where your actions and those of competitors affect market share, room demand, and pricing elasticity."
                    />
                    <Mechanic 
                      title="Guest Satisfaction System" 
                      description="Multi-faceted satisfaction metrics based on room quality, staff interactions, facilities, and overall experience."
                    />
                    <Mechanic 
                      title="Staff Development" 
                      description="Hire, train, and promote staff members who gain skills and experience over time, with unique personalities and specialties."
                    />
                  </div>
                  <div className="space-y-4">
                    <Mechanic 
                      title="Seasonal Events" 
                      description="Weather patterns, holidays, and local events that create opportunities and challenges throughout the year."
                    />
                    <Mechanic 
                      title="Reputation Management" 
                      description="Your hotel's reputation evolves based on guest reviews, marketing efforts, and facility quality, affecting future bookings."
                    />
                    <Mechanic 
                      title="Financial Analytics" 
                      description="Detailed reports on revenue streams, expenses, ROI calculations, and forecasting tools to optimize your business strategy."
                    />
                  </div>
                </div>
              </motion.div>
            </TabContent>
          )}
          
        </AnimatePresence>
      </div>
      
      {/* Call to action */}
      <motion.div 
        className="mt-16 md:mt-24 bg-gradient-to-r from-blue-600 to-indigo-700 py-12 md:py-16 relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTZWMGg2djMwem0tNiAwSDI0VjBoNnYzMHoiLz48cGF0aCBkPSJNMzAgMzZWMjRIMHY2aDMwem0wLTZWMjRoMzB2NkgzMHoiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Ready to Build Your Hotel Empire?
          </motion.h2>
          <motion.p 
            className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Join thousands of players already competing for hotel dominance
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <button
              onClick={() => navigate('/register')} 
              className="px-8 py-3 bg-white text-blue-700 rounded-lg font-medium transition-colors shadow-lg hover:bg-white/90"
            >
              Create Account
            </button>
            <button
              onClick={() => navigate('/login')} 
              className="px-8 py-3 bg-blue-800/50 hover:bg-blue-800/70 text-white rounded-lg font-medium transition-colors border border-white/20"
            >
              Log In
            </button>
          </motion.div>
        </div>
      </motion.div>
      
          <Footer />
    
      </div>
  );
};

export default About;
